import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { newsList } from "@/mocks/news";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function NewsDetailPage() {
  const { id } = useParams();
  const newsId = Number(id);
  const item = newsList.find((news) => news.id === newsId);

  const related = useMemo(() => {
    if (!item) return [];
    const sorted = [...newsList].sort((a, b) => b.date.localeCompare(a.date));
    const sameCategory = sorted.filter((n) => n.id !== item.id && n.category === item.category);
    const fallback = sorted.filter((n) => n.id !== item.id && n.category !== item.category);
    return [...sameCategory, ...fallback].slice(0, 3);
  }, [item]);

  if (!item) {
    return (
      <div className="pt-24 md:pt-28">
        <div className="max-w-7xl mx-auto w-full px-4 md:px-8 py-24 flex flex-col items-center text-center">
          <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center mb-5">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900">
            资讯未找到
          </h1>
          <p className="mt-2 text-sm text-foreground-500">你访问的资讯可能已被移除或链接有误。</p>
          <Link
            to="/news"
            className="mt-6 inline-flex items-center gap-2 px-5 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </span>
            返回资讯列表
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 md:pt-28">
      <article className="max-w-4xl mx-auto w-full px-4 md:px-8 py-8 md:py-12">
        <Breadcrumb
          items={[{ label: "首页", to: "/" }, { label: "资讯列表", to: "/news" }, { label: item.category }]}
        />

        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <span className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-800 text-xs font-medium">
            {item.category}
          </span>
          <span className="text-xs text-foreground-500 flex items-center gap-1.5">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-line text-xs"></i>
            </span>
            发布于 {item.date}
          </span>
        </div>

        <h1 className="mt-4 font-heading text-2xl md:text-4xl font-semibold text-foreground-950 leading-snug">
          {item.title}
        </h1>

        <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">{item.summary}</p>

        <div className="mt-8 w-full aspect-video rounded-lg overflow-hidden bg-background-200 border border-background-200">
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} - 叶山小百合资讯`}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div className="mt-8 space-y-5">
          {item.content.map((paragraph, index) => (
            <p key={index} className="text-sm md:text-base text-foreground-700 leading-loose">
              {paragraph}
            </p>
          ))}
        </div>

        <div className="mt-10 pt-6 border-t border-background-200">
          <Link
            to="/news"
            className="inline-flex items-center gap-2 px-5 h-11 rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </span>
            返回资讯列表
          </Link>
        </div>
      </article>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto w-full px-4 md:px-8 pb-16">
          <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-6">
            相关资讯推荐
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {related.map((news) => (
              <Link
                key={news.id}
                to={`/news/detail/${news.id}`}
                className="group flex gap-4 p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <span className="w-24 h-20 rounded-md overflow-hidden bg-background-200 shrink-0">
                  <img
                    src={news.cover}
                    alt={news.title}
                    title={`${news.title} - 叶山小百合资讯`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </span>
                <span className="min-w-0 flex flex-col justify-center">
                  <span className="text-xs text-foreground-500 mb-1">{news.date}</span>
                  <span className="text-sm font-medium text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {news.title}
                  </span>
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}